// RankingSection.js
import React from 'react';
import { TrendingUp, TrendingDown, Crown, ChevronRight } from 'lucide-react';
import { getCoinLogoPath } from '../../utils/coinLogo';

// 백엔드 응답 스키마:
// { timeFrame: "1d"|"8h", rankings: [{ ticker, nameKr, nameEn, iconUrl, score, runAt }] }
const RankingSection = ({ rankingData, onStockClick, onOpenRanking }) => {
  const rankings = rankingData?.rankings || [];

  // 상위 3개만 표시
  const topThree = rankings.slice(0, 3);

  // runAt 최댓값
  const getLatestDateTime = () => {
    const dates = rankings
      .map((item) => item.runAt)
      .filter((d) => d != null);
    if (dates.length === 0) return null;
    return new Date(Math.max(...dates.map((d) => new Date(d).getTime())));
  };

  const latestDateTime = getLatestDateTime();

  // 코인 로고 경로 — utils/coinLogo와 일치 (검색 모달 / 즐겨찾기 / 랭킹 동일 경로)
  const getLogoSrc = getCoinLogoPath;

  // 순위 아이콘
  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" fill="currentColor" />;
    if (rank === 2) return <Crown className="w-5 h-5 text-blue-200" fill="currentColor" />;
    if (rank === 3) return <Crown className="w-5 h-5 text-yellow-200" fill="currentColor" />;
    return null;
  };

  return (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <TrendingUp size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">오늘의 AI 코인 랭킹</h2>
      </div>

      {/* 상위 3개 카드 */}
      <div className="space-y-3 mb-4">
        {topThree.map((coin, index) => {
          const logoSrc = getLogoSrc(coin);
          return (
            <div
              key={coin.ticker}
              className={`bg-white/80 backdrop-blur-sm rounded-2xl hover:shadow-xl transition-all duration-300 p-4 md:p-5 border cursor-pointer ${
                index < 3
                  ? 'border-blue-200 shadow-lg shadow-blue-100/50'
                  : 'border-slate-200 shadow-md hover:border-blue-200'
              }`}
              onClick={() => onStockClick(coin.ticker)}
            >
              {/* 모바일 레이아웃 */}
              <div className="md:hidden flex items-center gap-3">
                <div className="flex-shrink-0">{getRankIcon(index + 1)}</div>

                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gray-100 shadow-md overflow-hidden">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={coin.ticker}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span
                      className="text-lg text-gray-400 w-full h-full items-center justify-center"
                      style={{ display: logoSrc ? 'none' : 'flex' }}
                    >
                      {coin.ticker.charAt(0)}
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 truncate">{coin.ticker}</h3>
                  <p className="text-xs text-gray-500 truncate">{coin.nameKr || coin.nameEn}</p>
                </div>

                <div className="flex-shrink-0 text-right">
                  <div className="text-xl font-bold text-gray-900">{coin.score}점</div>
                </div>
              </div>

              {/* 데스크톱 레이아웃 */}
              <div className="hidden md:flex items-center gap-4">
                <div className="flex items-center justify-center min-w-[50px]">
                  <div className="flex flex-col items-center">
                    {getRankIcon(index + 1)}
                    <span className="text-xs text-gray-500 mt-1">#{index + 1}</span>
                  </div>
                </div>

                <div className="relative">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center bg-gray-100 shadow-lg overflow-hidden">
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={coin.ticker}
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span
                      className="text-xl text-gray-400 w-full h-full items-center justify-center"
                      style={{ display: logoSrc ? 'none' : 'flex' }}
                    >
                      {coin.ticker.charAt(0)}
                    </span>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900">{coin.ticker}</h3>
                  <p className="text-sm text-gray-600 truncate">{coin.nameKr || coin.nameEn}</p>
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900 mb-1">{coin.score}점</div>
                  <div
                    className={`flex items-center justify-end gap-1 text-sm ${
                      coin.score >= 50 ? 'text-green-500' : 'text-red-500'
                    }`}
                  >
                    {coin.score >= 50 ? (
                      <TrendingUp className="w-4 h-4" />
                    ) : (
                      <TrendingDown className="w-4 h-4" />
                    )}
                    <span>AI 분석</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-gray-400 text-right mt-2">
        {latestDateTime ? new Date(latestDateTime).toLocaleString('ko-KR') : 'N/A'}
      </div>

      <button onClick={onOpenRanking} className="view-full-button mt-2">
        <span>전체 랭킹 보기</span>
        <ChevronRight size={16} />
      </button>
    </div>
  );
};

export default RankingSection;
